import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'

import { GeneralSetting } from '@apis/index'
import { RootState } from '@store/index'
import { Helper } from '@utils/index'

import Column from '@components/column'
import Wrapper from '@components/wrapper'
import Title from '@components/title'
import Fields from '@components/fields'
import Button from '@components/button'
import Row from '@components/row'
import useIsMobile from '@hooks/useIsMobile'

interface PageFormProps {
  title: string
  Key: string
}

const PageForm: React.FC<PageFormProps> = ({ title, Key }) => {
  const isMobile = useIsMobile()
  const stateKey = Helper.toCamelCase(title)
  const seoStateKey = `${stateKey}Seo`

  const pageData = useSelector(
    (state: RootState) => state?.[stateKey]?.data ?? {},
  )
  const seoData = useSelector(
    (state: RootState) => state?.[seoStateKey]?.data ?? {},
  )

  const [pageInitialValue, setPageInitialValue] = useState({ [Key]: '' })
  const [seoInitialValue, setSeoInitialValue] = useState({
    title: '',
    keyword: '',
    description: '',
    ogImage: null as any,
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        await GeneralSetting.getByKey({ id: Key, state: stateKey })
        await GeneralSetting.getByKey({ id: `${Key}-seo`, state: seoStateKey })
      } catch (error) {
        console.error('Fetch Error:', error)
      }
    }
    if (Key) fetchData()
  }, [Key, stateKey, seoStateKey])

  useEffect(() => {
    if (typeof pageData.value === 'string') {
      setPageInitialValue({ [Key]: pageData.value })
    }
  }, [pageData, Key])

  useEffect(() => {
    if (seoData?.value) {
      try {
        const parsedSeo = JSON.parse(seoData.value)
        setSeoInitialValue({
          title: parsedSeo.title || '',
          keyword: parsedSeo.keyword || '',
          description: parsedSeo.description || '',
          ogImage: seoData.file || null,
        })
      } catch (error) {
        console.error('SEO parse error', error)
      }
    }
  }, [seoData])

  const handlePageSubmit = async (values: any) => {
    try {
      const payload = {
        key: Key,
        value: values[Key],
        title,
      }

      if (pageData?.id) {
        await GeneralSetting.update({
          id: pageData.id,
          data: payload,
          state: stateKey,
        })
      } else {
        await GeneralSetting.create({ data: payload, state: stateKey })
      }

      toast.success('Page content saved successfully')
    } catch (error) {
      console.error('Submit Error:', error)
      toast.error('Failed to save page content')
    }
  }

  const handleSeoSubmit = async (values: any) => {
    try {
      const payload: any = {
        key: `${Key}-seo`,
        value: JSON.stringify(values),
      }

      if (values.ogImage) {
        payload.file = {
          base64: values.ogImage.base64,
          extension: values.ogImage.extension,
        }
      }

      if (seoData?.id) {
        await GeneralSetting.update({
          id: seoData.id,
          data: payload,
          state: seoStateKey,
        })
      } else {
        await GeneralSetting.create({ data: payload, state: seoStateKey })
      }

      toast.success('SEO settings saved successfully.')
    } catch (error) {
      console.error('SEO Submit Error:', error)
      toast.error('Failed to save SEO settings')
    }
  }

  return (
    <Fragment>
      <Column>
        {/* Page Content */}
        <Column>
          <Title textone={title} texttwo="Details" />
          <Wrapper>
            <Formik
              initialValues={pageInitialValue}
              enableReinitialize
              onSubmit={handlePageSubmit}
            >
              <Form>
                <Column>
                  <Fields.Editor name={Key} />
                  <Button
                    type="submit"
                    title="Save"
                    width={16}
                    height={45}
                    justify="flex-end"
                  />
                </Column>
              </Form>
            </Formik>
          </Wrapper>
        </Column>

        {/* SEO Settings */}
        <Wrapper>
          <Column>
            <Title textone="SEO" texttwo="Settings" />
            <Formik
              initialValues={seoInitialValue}
              enableReinitialize
              onSubmit={handleSeoSubmit}
            >
              <Form>
                <Row
                  styleRow={`align-items:${isMobile ? 'stretch' : 'center'}`}
                >
                  <div style={{ flex: 2 }}>
                    <Column>
                      <Fields.Input
                        name="title"
                        label="Meta Title"
                        placeholder="Enter Meta Title"
                      />
                      <Fields.Input
                        name="keyword"
                        label="Meta Keyword"
                        placeholder="Enter Meta Keyword"
                      />
                      <Fields.TextArea
                        name="description"
                        label="Meta Description"
                        placeholder="Enter Meta Description"
                      />
                    </Column>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Fields.Upload
                      name="ogImage"
                      variant="banner"
                      label="OG Image (Recommended 1200x630 px)"
                      isOgImage={true}
                    />
                  </div>
                </Row>
                <br />
                <Button
                  type="submit"
                  title="Save SEO"
                  justify="end"
                  width={16}
                  height={45}
                />
              </Form>
            </Formik>
          </Column>
        </Wrapper>
      </Column>
    </Fragment>
  )
}

export default PageForm
